import { useEffect, useMemo, useRef, useState } from 'react';
import {
  findAnalyzedMovePlyForJustPlayed,
  type AnalyzedMoveForReview,
} from './buildMoveQualitySquareStyles';

type UseMoveQualityBadgePlyOptions = {
  /** Hide the badge this long after it becomes visible (autoplay step minus animation). */
  displayDurationMs?: number;
  /** Temporarily hide during refutation feedback; restores afterward on the same ply. */
  suppressed?: boolean;
};

/**
 * Track which analyzed ply should show the move-quality badge for the move
 * that just landed. Can appear, hide for refutation, then appear again.
 */
export function useMoveQualityBadgePly(
  analyzedMoves: readonly AnalyzedMoveForReview[],
  movesPlayedCount: number,
  animationDurationMs: number,
  { displayDurationMs, suppressed = false }: UseMoveQualityBadgePlyOptions = {},
): number | null {
  const targetPly = useMemo(
    () => findAnalyzedMovePlyForJustPlayed(analyzedMoves, movesPlayedCount),
    [analyzedMoves, movesPlayedCount],
  );
  const [readyPly, setReadyPly] = useState<number | null>(null);
  const [revealedPly, setRevealedPly] = useState<number | null>(null);
  const displayExpiredRef = useRef(false);
  const wasSuppressedRef = useRef(false);

  useEffect(() => {
    if (targetPly === null) {
      displayExpiredRef.current = false;
      setReadyPly(null);
      setRevealedPly(null);
      return undefined;
    }

    displayExpiredRef.current = false;
    setReadyPly(null);
    setRevealedPly(null);

    const showTimer = window.setTimeout(() => {
      setReadyPly(targetPly);
    }, Math.max(0, animationDurationMs));

    return () => window.clearTimeout(showTimer);
  }, [animationDurationMs, targetPly]);

  useEffect(() => {
    if (readyPly !== targetPly || targetPly === null) {
      return undefined;
    }

    if (suppressed) {
      wasSuppressedRef.current = true;
      setRevealedPly(null);
      return undefined;
    }

    if (wasSuppressedRef.current) {
      wasSuppressedRef.current = false;
      displayExpiredRef.current = false;
    }

    if (displayExpiredRef.current) {
      return undefined;
    }

    setRevealedPly(targetPly);

    if (displayDurationMs == null || displayDurationMs <= 0) {
      return undefined;
    }

    const hideTimer = window.setTimeout(() => {
      displayExpiredRef.current = true;
      setRevealedPly((current) => (current === targetPly ? null : current));
    }, displayDurationMs);

    return () => window.clearTimeout(hideTimer);
  }, [displayDurationMs, readyPly, suppressed, targetPly]);

  if (
    suppressed ||
    targetPly === null ||
    readyPly !== targetPly ||
    displayExpiredRef.current ||
    revealedPly !== targetPly
  ) {
    return null;
  }

  return targetPly;
}
